import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { forkJoin, Subject, Subscription, timer } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { CanDeactivateServiceWrapper } from '../../_helpers/can-deactivate-service-wrapper';
import {
  Campaign,
  OmpCtaRequiredFields,
  OmpFieldKind,
  OmpFieldLabels,
  OmpHistoryLabel,
  OmpHistoryStep,
  OmpHostMessageType,
  OmpHostSelectPayload,
  OmpLabelIssue,
  OmpMoment,
  OmpPage,
  OmpPageSectionItem,
  OmpPreviewFaq,
  OmpPreviewFaqTopic,
  OmpPreviewReason,
  OmpRevealTarget,
  OmpRuntimeSection,
  OmpSection,
  OmpSectionDef,
  OmpSectionIssueKind,
  OmpSectionKey,
  OmpSectionPosition,
  OmpStyleField,
  OmpTab,
  PortalSettings,
  Style,
  StyleLanguage,
  User,
} from '../../_models';
import {
  AlertService,
  CampaignService,
  FaqService,
  FaqTopicService,
  LoaderService,
  OmpEditorHistoryService,
  OmpPreviewBridgeService,
  StyleService,
  UserService,
} from '../../_services';
import { fontFamilies } from '../../web-builder/editorConfig';
import {
  OmpButtonGroups,
  OmpButtonLabelMaxLength,
  OmpButtonShapeGroup,
  OmpColumnsChildField,
  OmpDefaultMoment,
  OmpMomentActionsField,
  OmpMomentKey,
  OmpMomentList,
  OmpPages,
  OmpPagesWithSections,
  OmpPickerSectionKeys,
  OmpRetiredSectionFields,
  OmpRetiredStyleVars,
  OmpSectionCatalog,
  OmpSectionRowHintFields,
  OmpSectionRowHintLength,
  OmpSidebarSections,
  OmpStyleVar,
  OmpTopSectionOrder,
  isOmpColumnChildKey,
  ompDefaultPageSections,
  ompMomentLabel,
  ompMomentSectionKey,
  ompMomentsForPage,
  ompPageHasMoment,
  ompPageHasMoments,
  ompSectionDef,
  ompSectionName,
} from '../order-management-portal.sections';
import { ompCtaEnabled, ompFieldGroupLabel, ompFieldIsEmpty, ompRequiredAnyOf, ompRequiredFields, ompSampleDataNote } from '../omp.utils';
import { OMP_PREVIEW_SUBDOMAIN_TYPE } from '../omp-preview-url';
import {
  getOmpSectionDefaults,
  OmpBrandOwnedStyleVars,
  OmpButtonShapeFields,
  OmpButtonStyleFields,
  OmpGlobalStyleFields,
  OmpPickerService,
  OmpStyleDefaults,
  ompMomentCardFromNextOrderDetails,
  paletteSectionDefaults,
  paletteStyleDefaults,
  seedOmpSectionData,
} from '../omp-sections';

function canonicalJson(value: any): string {
  return JSON.stringify(value, (key, entry) => {
    let result = entry;
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      result = {};
      Object.keys(entry)
        .sort()
        .forEach((name) => {
          result[name] = entry[name];
        });
    }
    return result;
  });
}

@Component({
  selector: 'app-omp-sidebar',
  templateUrl: './omp-sidebar.component.html',
  styleUrls: ['./omp-sidebar.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0, paddingTop: '0', paddingBottom: '0', overflow: 'hidden' }),
        animate('220ms ease', style({ height: '*', opacity: 1, paddingTop: '*', paddingBottom: '*' })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease', style({ height: '0', opacity: 0, paddingTop: '0', paddingBottom: '0' })),
      ]),
    ]),
  ],
})
export class OmpSidebarComponent implements OnInit, OnChanges, OnDestroy {
  public sections = OmpSidebarSections;
  public ompSection = OmpSection;
  public ompTab = OmpTab;

  public styleVar = OmpStyleVar;
  public fontFamilies = fontFamilies;

  @Input() activeTab: OmpTab = OmpTab.Sections;
  @Input() selectedMoment: OmpMoment = OmpMoment.None;
  @Input() selectedPage: OmpPage = OmpPage.Home;

  public pagesWithSections = OmpPagesWithSections;
  public pageMoments: { [page: string]: { [momentKey: string]: OmpRuntimeSection[] } } = {};
  public addableSections: OmpSectionDef[] = OmpPickerSectionKeys.map((k) => OmpSectionCatalog[k]).filter((d) => !!d);
  public settingsStack: OmpRuntimeSection[] = [];
  public isStaff = false;

  private _stylesChanged = false;
  private _sectionsChanged = false;
  private stylesBaseline = '';
  private sectionsBaseline = '';
  private canDeactivateService = CanDeactivateServiceWrapper.getObject();
  private formChangesSub: Subscription = null;
  private applyingHistory = false;
  private historyQueued = false;
  private historyLabel: string = null;
  public saving = false;

  public labelIssueList: OmpLabelIssue[] = [];

  private previewFaqTopics: OmpPreviewFaqTopic[] = [];
  private previewSelectedFaqs: OmpPreviewFaq[] = [];
  private lastRequestedFaqSignature: string = null;
  private lastSentFaqSignature: string = null;

  public pickerSelectedKey: string = null;
  public openSettingsSection: OmpRuntimeSection = null;

  public expandedSection: OmpSection = OmpSection.PageHeader;

  public expandedButtonStyle: string = OmpButtonShapeGroup;

  public fieldKind = OmpFieldKind;
  public styleFields = OmpGlobalStyleFields;
  public buttonStyleGroups: { key: string; label: string; help: string; fields: OmpStyleField[] }[] = [
    { key: OmpButtonShapeGroup, label: 'Button Shape', help: 'Corner shape shared by every button.', fields: OmpButtonShapeFields },
    ...OmpButtonGroups.map((bs) => ({
      key: bs.key as string,
      label: bs.label,
      help: bs.help,
      fields: OmpButtonStyleFields.map((f) => ({ ...f, key: f.key ? bs.key + '_' + f.key : undefined })),
    })),
  ];

  public form: FormGroup;
  private formDefaults = OmpStyleDefaults;

  private campaign: Campaign = null;
  public styleId: string | number = null;
  public brandVariables: any = {};
  private ompOverrides: any = {};
  private variablesNeedCleanup = false;
  private themeMigrated = false;

  private booleanKeys: string[] = [
    OmpStyleVar.ShowHeaderSeparatorLine,
    OmpStyleVar.EnableStickyNavigationalBlocks,
    OmpStyleVar.OrderPriceOnListPage,
  ];

  public selectedImageControl: string = null;

  public galleryCropperDimensions = { width: 1600, height: 0 };

  @Output() requestPage = new EventEmitter<{ page: OmpPage; moment: OmpMoment }>();
  @Output() syncMoments = new EventEmitter<void>();
  private pendingIssue: OmpLabelIssue = null;

  private previewPush$ = new Subject<OmpPreviewReason>();
  private pendingReasons: { [reason: string]: boolean } = {};
  private pendingDataPage: OmpPage = OmpPage.Home;
  private pendingDataMoment: OmpMoment = OmpMoment.None;
  private destroyed$ = new Subject<void>();
  private pendingReveal: OmpRevealTarget = null;
  private lastSentSelectionUid: string = null;
  private lastSentHoverUid: string = null;
  private pendingDelete: OmpRuntimeSection = null;
  private pendingInsertIndex: number = null;
  private uidCounter = 0;
  private styleLoaded = false;
  private previewConnected = false;
  public pickerZone: OmpSectionPosition = null;

  private get stylesChanged(): boolean {
    return this._stylesChanged;
  }
  private set stylesChanged(value: boolean) {
    this._stylesChanged = value && this.stylesSnapshot() !== this.stylesBaseline;
    this.emitDirty();
    if (value) {
      this.captureHistory();
    }
  }
  private get sectionsChanged(): boolean {
    return this._sectionsChanged;
  }
  private set sectionsChanged(value: boolean) {
    this._sectionsChanged = value && this.sectionsSnapshot() !== this.sectionsBaseline;
    this.labelIssueList = this.labelIssues();
    this.emitDirty();
    if (value) {
      this.captureHistory();
    }
  }

  public get labelIssueMessages(): string[] {
    return this.labelIssueList.map((issue) => {
      let detail = `${issue.length}/${OmpButtonLabelMaxLength} characters`;
      if (issue.kind !== OmpSectionIssueKind.LabelLength) {
        detail = 'required';
      }
      return `${ompSectionDef(issue.sectionKey).name} — ${this.fieldLabel(issue.field)}: ` +
        `${detail} (${this.issueLocation(issue)})`;
    });
  }

  private issueLocation(issue: OmpLabelIssue): string {
    let page = '';
    OmpPages.forEach((entry) => {
      if (entry.key === issue.page) {
        page = entry.label;
      }
    });
    let location = page;
    if (ompPageHasMoments(issue.page)) {
      location = page + ' — ' + ompMomentLabel(issue.moment);
    }
    return location;
  }

  private fieldLabel(field: string): string {
    let label = OmpFieldLabels[field];
    if (field.indexOf('|') !== -1) {
      label = ompFieldGroupLabel(field.split('|'), (name) => this.fieldLabel(name));
    }
    if (!label) {
      label = field
        .replace('_label', '')
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return label;
  }

  public get hasChanges(): boolean {
    return this._stylesChanged || this._sectionsChanged;
  }

  public get canSave(): boolean {
    return this.hasChanges && this.labelIssueList.length === 0;
  }

  private stylesSnapshot(): string {
    return canonicalJson(this.buildOverrides());
  }

  private sectionsSnapshot(): string {
    return canonicalJson(this.buildSectionsConfig());
  }

  private resetStylesBaseline(): void {
    this.stylesBaseline = this.themeMigrated ? '' : this.stylesSnapshot();
    this.stylesChanged = this.themeMigrated;
  }

  private markSectionsSaved(): void {
    this.sectionsBaseline = this.sectionsSnapshot();
    this.sectionsChanged = false;
  }

  private emitDirty(): void {
    if (this.canDeactivateService) {
      this.canDeactivateService.setFormDirty(this.hasChanges);
    }
  }

  constructor(
    private fb: FormBuilder,
    private campaignService: CampaignService,
    private styleService: StyleService,
    private modalService: NgxSmartModalService,
    private alertService: AlertService,
    private loaderService: LoaderService,
    private previewBridge: OmpPreviewBridgeService,
    private faqService: FaqService,
    private faqTopicService: FaqTopicService,
    private userService: UserService,
    public picker: OmpPickerService,
    private history: OmpEditorHistoryService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({ ...this.formDefaults });
  }

  ngOnInit() {
    this.stylesBaseline = this.stylesSnapshot();
    this.formChangesSub = this.form.valueChanges.subscribe(() => {
      if (!this.applyingHistory) {
        this.stylesChanged = true;
      }
      this.pushPreview(OmpPreviewReason.Theme);
    });

    this.previewPush$
      .pipe(
        debounce(() => timer(this.pendingReasons[OmpPreviewReason.Structure] ? 0 : 90)),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => this.sendPreviewUpdate());

    this.loadPreviewFaqTopics();

    this.userService
      .getCurrent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user: User) => {
        this.isStaff = !!(user && user.is_staff);
      });

    this.previewBridge.ready$.pipe(takeUntil(this.destroyed$)).subscribe((ready: boolean) => {
      this.previewConnected = ready;
      this.sendPreviewInitWhenLoaded();
    });
    this.picker.openImageModal = () => this.modalService.getModal('ompImageGalleryModal').open();
    this.picker.closeImageModal = () => this.modalService.getModal('ompImageGalleryModal').close();
    this.picker.openProductModal = () => this.modalService.getModal('ompProductModal').open();
    this.picker.closeProductModal = () => this.modalService.getModal('ompProductModal').close();
    this.picker.openIconModal = () => this.modalService.getModal('ompIconModal').open();
    this.picker.closeIconModal = () => this.modalService.getModal('ompIconModal').close();
    this.loadStyle();
  }

  ngOnChanges(changes: SimpleChanges) {
    const pageChanged = changes.selectedPage && !changes.selectedPage.firstChange;
    const momentChanged = changes.selectedMoment && !changes.selectedMoment.firstChange;
    if (pageChanged || momentChanged) {
      this.closeSettings();
      if (this.pendingIssue) {
        this.openIssueSection(this.pendingIssue);
        this.pendingIssue = null;
      }
      this.applyPendingReveal();
    }
  }

  ngOnDestroy() {
    if (this.formChangesSub) {
      this.formChangesSub.unsubscribe();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
    this.canDeactivateService.setFormDirty(false);
  }

  get campaignId() {
    return this.campaign ? this.campaign.id : null;
  }

  private loadStyle() {
    this.campaign = this.campaignService.getCurrentCampaign();
    if (this.campaign) {
      this.setSectionsFromCampaign();
      let apiCall = this.styleService.create({
        campaign: this.campaign.id,
        name: `Style for ${this.campaign.name} (${this.campaign.id})`,
        language: StyleLanguage.CSS,
      });
      if (this.campaign.styles && this.campaign.styles.length > 0) {
        apiCall = this.styleService.get(this.campaign.styles[0]);
      }

      this.loaderService.show();
      apiCall.subscribe(
        (style: Style) => {
          this.loaderService.hide();
          this.styleLoaded = true;
          if (style) {
            this.styleId = style.id;
            this.campaign.styles = [style.id as number];
            this.campaignService.currentCampaignUpdated(this.campaign);
            this.themeMigrated = this.splitStyle(style);
            this.form.patchValue(this.resolveFormValues());
            this.resetStylesBaseline();
          }
          this.resetHistory();
          this.sendPreviewInitWhenLoaded();
        },
        (error) => {
          this.loaderService.hide();
          this.styleLoaded = true;
          this.alertService.error('Failed to load brand styles.');
          this.resetHistory();
          this.sendPreviewInitWhenLoaded();
        }
      );
    } else {
      this.styleLoaded = true;
      this.resetHistory();
      this.sendPreviewInitWhenLoaded();
    }
  }

  labelIssues(): OmpLabelIssue[] {
    const issues: OmpLabelIssue[] = [];
    this.forEachPageMoment((list, page, moment) => this.collectLabelIssues(list, page, moment, issues));
    return issues;
  }

  private forEachPageMoment(visit: (list: OmpRuntimeSection[], page: OmpPage, moment: OmpMoment) => void) {
    this.pagesWithSections.forEach((page) => {
      ompMomentsForPage(page).forEach((moment) => visit(this.sectionsFor(page, moment), page, moment));
    });
  }

  private collectLabelIssues(list: OmpRuntimeSection[], page: OmpPage, moment: OmpMoment, issues: OmpLabelIssue[]) {
    const sections = list ? list : [];
    sections.forEach((section, index) => {
      if (section.visible) {
        this.collectDataLabelIssues(section.key, section.data, page, moment, index, -1, issues);
      }
    });
  }

  private collectDataLabelIssues(
    key: string,
    data: any,
    page: OmpPage,
    moment: OmpMoment,
    sectionIndex: number,
    childIndex: number,
    issues: OmpLabelIssue[]
  ) {
    const fields = data ? data : {};
    Object.keys(fields).forEach((field) => {
      const value = fields[field];
      if (field.indexOf('_label') > 0 && typeof value === 'string' && value.length > OmpButtonLabelMaxLength) {
        issues.push({
          page: page,
          moment: moment,
          sectionKey: key,
          sectionIndex: sectionIndex,
          childIndex: childIndex,
          field: field,
          kind: OmpSectionIssueKind.LabelLength,
          length: value.length,
        });
      }
    });
    const actions = Array.isArray(fields[OmpMomentActionsField]) ? fields[OmpMomentActionsField] : [];
    actions.forEach((action) => {
      const label = action && typeof action.label === 'string' ? action.label : '';
      if (action.is_visible !== false && label.length > OmpButtonLabelMaxLength) {
        issues.push({
          page: page,
          moment: moment,
          sectionKey: key,
          sectionIndex: sectionIndex,
          childIndex: childIndex,
          field: action.key + '_label',
          kind: OmpSectionIssueKind.LabelLength,
          length: label.length,
        });
      }
    });
    const pushRequired = (field: string) => {
      if (ompFieldIsEmpty(fields[field])) {
        issues.push({
          page: page,
          moment: moment,
          sectionKey: key,
          sectionIndex: sectionIndex,
          childIndex: childIndex,
          field: field,
          kind: OmpSectionIssueKind.Required,
          length: 0,
        });
      }
    };
    ompRequiredFields(key).forEach(pushRequired);
    const anyOf = ompRequiredAnyOf(key);
    if (anyOf.length > 0 && anyOf.every((field) => ompFieldIsEmpty(fields[field]))) {
      issues.push({
        page: page,
        moment: moment,
        sectionKey: key,
        sectionIndex: sectionIndex,
        childIndex: childIndex,
        field: anyOf.join('|'),
        kind: OmpSectionIssueKind.RequiredAnyOf,
        length: 0,
      });
    }
    if (ompCtaEnabled(fields)) {
      OmpCtaRequiredFields.forEach(pushRequired);
    }
    const children = Array.isArray(fields[OmpColumnsChildField]) ? fields[OmpColumnsChildField] : [];
    children.forEach((child, index) => {
      if (child) {
        this.collectDataLabelIssues(child.key, child.data, page, moment, sectionIndex, index, issues);
      }
    });
  }

  private showLabelIssue(issue: OmpLabelIssue) {
    const samePage =
      this.selectedPage === issue.page &&
      (!ompPageHasMoments(issue.page) || this.selectedMoment === issue.moment);
    if (samePage) {
      this.openIssueSection(issue);
    } else {
      this.pendingIssue = issue;
      this.requestPage.emit({ page: issue.page, moment: issue.moment });
    }
  }

  private openIssueSection(issue: OmpLabelIssue) {
    const list = this.currentSections();
    const section = issue.sectionIndex >= 0 && issue.sectionIndex < list.length ? list[issue.sectionIndex] : null;
    if (section) {
      this.openSectionSettings(section);
      const children = this.childSectionsOf(section);
      if (issue.childIndex >= 0 && issue.childIndex < children.length) {
        this.openChildSettings(children[issue.childIndex]);
      }
    }
  }

  childSectionsOf(section: OmpRuntimeSection): OmpPageSectionItem[] {
    const data = section && section.data ? section.data : {};
    const children = data[OmpColumnsChildField];
    return Array.isArray(children) ? children : [];
  }

  save(onComplete?: () => void) {
    const issues = this.labelIssues();
    if (issues.length > 0) {
      this.showLabelIssue(issues[0]);
    } else {
      this.saveChanges(onComplete);
    }
  }

  private saveChanges(onComplete?: () => void) {
    const calls = [];

    let overrides = null;
    let cleanedVariables = null;
    if (this.styleId && (this.stylesChanged || this.variablesNeedCleanup)) {
      overrides = this.buildOverrides();
      const stylePatch: Partial<Style> = { omp_theme: overrides };
      if (this.variablesNeedCleanup) {
        cleanedVariables = { ...this.brandVariables };
        stylePatch.variables = cleanedVariables;
      }
      calls.push(this.styleService.patch(this.styleId, stylePatch));
    }

    const campaignPatch: Partial<Campaign> = {};
    let sectionsConfig = null;
    if (this.sectionsChanged) {
      sectionsConfig = this.buildSectionsConfig();
      campaignPatch.portal_settings = { sections_config: sectionsConfig } as PortalSettings;
    }
    if (this.campaign && Object.keys(campaignPatch).length > 0) {
      calls.push(this.campaignService.patch(this.campaign.id, campaignPatch));
    }

    if (calls.length === 0) {
      if (onComplete) {
        onComplete();
      }
    } else {
      this.loaderService.show();
      this.saving = true;
      forkJoin(calls).subscribe(
        () => {
          this.loaderService.hide();
          this.saving = false;
          if (overrides) {
            this.ompOverrides = overrides;
            this.themeMigrated = false;
            this.resetStylesBaseline();
            if (cleanedVariables) {
              this.variablesNeedCleanup = false;
            }
          }
          if (this.campaign && sectionsConfig) {
            let existing: Partial<PortalSettings> = {};
            if (this.campaign.portal_settings) {
              existing = this.campaign.portal_settings;
            }
            this.campaign.portal_settings = { ...existing, sections_config: sectionsConfig } as PortalSettings;
            this.markSectionsSaved();
          }
          this.campaignService.currentCampaignUpdated(this.campaign);
          this.resetHistory();
          this.alertService.success('Portal styles saved.', true);
          if (onComplete) {
            onComplete();
          }
        },
        () => {
          this.loaderService.hide();
          this.saving = false;
          this.alertService.error('Failed to save portal styles. Your changes and undo history are kept, so you can Save again.');
        }
      );
    }
  }

  private buildSectionData(section: OmpRuntimeSection, page: string, moment: OmpMoment) {
    const defaults = getOmpSectionDefaults(section.key, page, moment);
    const source = section.data ? section.data : {};
    const data: { [field: string]: any } = {};
    if (defaults) {
      Object.keys(defaults).forEach((field) => {
        data[field] = source[field] !== undefined ? source[field] : defaults[field];
      });
    }
    if (data[OmpColumnsChildField] !== undefined) {
      const children = this.buildChildSections(data[OmpColumnsChildField], page, moment);
      data[OmpColumnsChildField] = children;
      data.column_count = children.length;
    }
    return data;
  }

  private buildChildSections(children: any, page: string, moment: OmpMoment) {
    const list = Array.isArray(children) ? children : [];
    return list.filter((child) => child && isOmpColumnChildKey(child.key)).map((child) => ({
      key: child.key,
      data: this.buildSectionData({ key: child.key, data: child.data, visible: true }, page, moment),
    }));
  }

  private buildPageSections(list: OmpRuntimeSection[], page: string, moment: OmpMoment) {
    return (list || []).map((section) => ({
      key: section.key,
      is_visible: section.visible,
      data: this.buildSectionData(section, page, moment),
    }));
  }

  private buildPreviewSections(list: OmpRuntimeSection[], page: string, moment: OmpMoment) {
    const serialized = this.buildPageSections(list, page, moment);
    return serialized.map((section, index) => {
      const source = (list || [])[index];
      const def = this.sectionDef(section.key);
      return {
        key: section.key,
        uid: source && source.uid ? source.uid : section.key + '#' + index,
        name: ompSectionName(section.key, page),
        is_visible: section.is_visible,
        deletable: def.deletable,
        data: section.data,
      };
    });
  }

  private buildPreviewSectionsConfig() {
    return this.buildConfig((list, page, moment) => this.buildPreviewSections(list, page, moment));
  }

  private buildSectionsConfig() {
    return this.buildConfig((list, page, moment) => this.buildPageSections(list, page, moment));
  }

  private buildConfig(build: (list: OmpRuntimeSection[], page: OmpPage, moment: OmpMoment) => any[]) {
    const config = {};
    this.pagesWithSections.forEach((page) => {
      if (ompPageHasMoments(page)) {
        const moments = {};
        ompMomentsForPage(page).forEach((moment) => {
          moments[OmpMomentKey[moment]] = build(this.sectionsFor(page, moment), page, moment);
        });
        config[page] = moments;
      } else {
        config[page] = build(this.sectionsFor(page, OmpMoment.None), page, OmpMoment.None);
      }
    });
    return config;
  }

  private buildOverrides() {
    const overrides = {};
    Object.keys(this.ompOverrides).forEach((key) => {
      if (this.formDefaults[key] === undefined) {
        overrides[key] = this.ompOverrides[key];
      }
    });
    const values = this.form.value;
    Object.keys(values).forEach((key) => {
      let value = values[key];
      if (typeof value === 'boolean') {
        value = value ? 'true' : 'false';
      }
      if (value !== null && value !== undefined && value !== '') {
        overrides[key] = value;
      }
    });
    return overrides;
  }

  private splitStyle(style: Style): boolean {
    this.brandVariables = style.variables ? { ...style.variables } : {};
    let overrides = {};
    const stored = style.omp_theme;
    if (stored && typeof stored === 'object') {
      overrides = { ...stored };
    }
    this.ompOverrides = overrides;
    this.variablesNeedCleanup = this.migrateFlatOmpVars();
    const themeRenamed = this.dropRetiredVars();
    return this.variablesNeedCleanup || themeRenamed;
  }

  private dropRetiredVars(): boolean {
    let renamed = false;
    Object.keys(OmpRetiredStyleVars).forEach((key) => {
      const replacement = OmpRetiredStyleVars[key];
      let value = this.brandVariables[key];
      if (this.ompOverrides[key] !== undefined) {
        value = this.ompOverrides[key];
      }
      if (replacement && value !== undefined && this.ompOverrides[replacement] === undefined) {
        this.ompOverrides[replacement] = value;
      }
      if (this.ompOverrides[key] !== undefined) {
        delete this.ompOverrides[key];
        renamed = true;
      }
      if (this.brandVariables[key] !== undefined) {
        delete this.brandVariables[key];
        this.variablesNeedCleanup = true;
      }
    });
    return renamed;
  }

  private renameRetiredFields(section: OmpRuntimeSection) {
    Object.keys(OmpRetiredSectionFields).forEach((field) => {
      const replacement = OmpRetiredSectionFields[field];
      if (section.data[field] !== undefined) {
        if (section.data[replacement] === undefined) {
          section.data[replacement] = section.data[field];
        }
        delete section.data[field];
      }
    });
  }

  private migrateFlatOmpVars(): boolean {
    let migrated = false;
    Object.keys(this.formDefaults).forEach((key) => {
      const isBrandOwned = OmpBrandOwnedStyleVars.indexOf(key) >= 0;
      const flatValue = this.brandVariables[key];
      if (!isBrandOwned && flatValue !== undefined) {
        if (this.ompOverrides[key] === undefined) {
          this.ompOverrides[key] = flatValue;
        }
        delete this.brandVariables[key];
        migrated = true;
      }
    });
    return migrated;
  }

  promptReset() {
    this.modalService.getModal('ompResetConfirmModal').open();
  }

  cancelReset() {
    this.modalService.getModal('ompResetConfirmModal').close();
  }

  promptRestoreBrandDefaults() {
    this.modalService.getModal('ompRestoreBrandModal').open();
  }

  cancelRestoreBrandDefaults() {
    this.modalService.getModal('ompRestoreBrandModal').close();
  }

  applyRestoreBrandDefaults() {
    this.modalService.getModal('ompRestoreBrandModal').close();
    const values = { ...paletteStyleDefaults(this.brandVariables) };
    OmpBrandOwnedStyleVars.forEach((key) => {
      const brandValue = this.brandVariables[key];
      if (brandValue !== null && brandValue !== undefined && brandValue !== '') {
        values[key] = brandValue;
      } else if (values[key] === undefined) {
        values[key] = this.formDefaults[key];
      }
    });
    values[OmpStyleVar.Logo] = this.campaignLogoUrl();
    this.labelNextStep(OmpHistoryLabel.RestoreBrand);
    this.form.patchValue(values);
    this.restoreSectionColors();
    this.stylesChanged = true;
    this.pushPreview(OmpPreviewReason.Structure);
  }

  private restoreSectionColors() {
    const applyToSection = (key: string, data: any, moment: OmpMoment) => {
      if (data) {
        const colors = paletteSectionDefaults(this.brandVariables, key, data.tone, moment);
        Object.keys(colors).forEach((field) => {
          if (data[field] !== undefined) {
            data[field] = colors[field];
          }
        });
        const children = Array.isArray(data[OmpColumnsChildField]) ? data[OmpColumnsChildField] : [];
        children.forEach((child) => {
          if (child) {
            applyToSection(child.key, child.data, moment);
          }
        });
      }
    };
    const apply = (list: OmpRuntimeSection[], moment: OmpMoment) => {
      const sections = list ? list : [];
      sections.forEach((section) => applyToSection(section.key, section.data, moment));
    };
    this.forEachPageMoment((list, page, moment) => apply(list, moment));
    this.sectionsChanged = true;
  }

  applyReset() {
    this.applyingHistory = true;
    this.form.reset(this.resolveFormValues());
    this.setSectionsFromCampaign();
    this.resetStylesBaseline();
    this.applyingHistory = false;
    this.modalService.getModal('ompResetConfirmModal').close();
    this.pushPreview(OmpPreviewReason.Theme);
    this.resetHistory();
  }

  undo() {
    this.applyHistoryStep(this.history.undo());
  }

  redo() {
    this.applyHistoryStep(this.history.redo());
  }

  private resetHistory() {
    this.historyLabel = null;
    this.history.reset(JSON.stringify(this.form.getRawValue()), JSON.stringify(this.pageMoments));
  }

  private labelNextStep(label: string) {
    if (!this.historyLabel) {
      this.historyLabel = label;
    }
  }

  private captureHistory() {
    if (this.history.ready && !this.applyingHistory && !this.historyQueued) {
      this.historyQueued = true;
      Promise.resolve().then(() => this.flushHistory());
    }
  }

  private flushHistory() {
    this.historyQueued = false;
    if (this.history.ready && !this.applyingHistory) {
      const styles = this.form.getRawValue();
      const stylesJson = JSON.stringify(styles);
      const sectionsJson = JSON.stringify(this.pageMoments);
      this.history.record(stylesJson, sectionsJson, this.stepLabel(styles, stylesJson));
    }
    this.historyLabel = null;
  }

  private stepLabel(styles: { [key: string]: any }, stylesJson: string): string {
    let label = this.historyLabel;
    const present = this.history.current;
    if (!label && present && present.styles !== stylesJson) {
      const before = JSON.parse(present.styles);
      const changed = Object.keys(styles).filter((key) => JSON.stringify(styles[key]) !== JSON.stringify(before[key]));
      if (changed.length) {
        label = this.styleLabel(changed[0]);
      }
    }
    if (!label) {
      label = OmpHistoryLabel.Sections;
      const crumbs = this.settingsBreadcrumb();
      if (crumbs.length) {
        label = crumbs[crumbs.length - 1];
      }
    }
    return label;
  }

  private styleLabel(key: string): string {
    let label = '';
    const scan = (fields: OmpStyleField[], prefix: string) => {
      fields.forEach((field) => {
        if (field.key === key && field.label) {
          label = prefix + field.label;
        }
        (field.checkboxes ? field.checkboxes : []).forEach((checkbox) => {
          if (checkbox.key === key) {
            label = prefix + checkbox.label;
          }
        });
      });
    };
    Object.keys(this.styleFields).forEach((section) => scan(this.styleFields[section], ''));
    this.buttonStyleGroups.forEach((group) => scan(group.fields, group.label + ': '));
    if (!label) {
      label = OmpHistoryLabel.Styles;
    }
    return label;
  }

  private visibilityLabel(section: OmpRuntimeSection): string {
    const action = section.visible ? OmpHistoryLabel.Show : OmpHistoryLabel.Hide;
    return action + ' ' + ompSectionName(section.key, this.selectedPage);
  }

  private applyHistoryStep(step: OmpHistoryStep) {
    if (step) {
      const target = this.settingsTarget();
      this.applyingHistory = true;
      this.pageMoments = JSON.parse(step.sections);
      this.form.patchValue(JSON.parse(step.styles));
      this.stylesChanged = true;
      this.sectionsChanged = true;
      this.applyingHistory = false;
      this.rebindSettings(target);
      this.pushPreview(OmpPreviewReason.Structure);
    }
  }

  private settingsTarget(): OmpRevealTarget {
    let target: OmpRevealTarget = null;
    const root = this.settingsStack.length ? this.settingsStack[0] : null;
    if (root && root.uid) {
      target = { page: this.selectedPage, moment: this.selectedMoment, uid: root.uid };
      if (this.settingsStack.length > 1) {
        const child = this.settingsStack[1];
        const index = this.childSectionsOf(root).findIndex((item) => item.data === child.data);
        if (index >= 0) {
          target.childIndex = index;
        }
      }
    }
    return target;
  }

  private rebindSettings(target: OmpRevealTarget) {
    this.settingsStack = [];
    this.openSettingsSection = null;
    this.cdr.detectChanges();
    if (target) {
      this.requestReveal(target);
      this.applyPendingReveal();
    }
    if (!this.openSettingsSection) {
      this.emitSelection();
    }
  }

  private derivedDefaults() {
    return { ...this.formDefaults, ...paletteStyleDefaults(this.brandVariables) };
  }

  private resolveFormValues() {
    const merged = this.derivedDefaults();
    Object.keys(this.formDefaults).forEach((key) => {
      let value = this.brandValueFor(key);
      const override = this.ompOverrides[key];
      if (override !== null && override !== undefined && override !== '') {
        value = override;
      }
      if (value === null || value === undefined || value === '') {
        value = merged[key];
      }
      if (value !== null && value !== undefined && value !== '') {
        if (this.booleanKeys.indexOf(key) >= 0) {
          merged[key] = value === true || value === 'true';
        } else {
          merged[key] = value;
        }
      }
    });
    return merged;
  }

  private brandValueFor(key: string) {
    let value = this.brandVariables[key];
    if (key === OmpStyleVar.Logo) {
      value = this.campaignLogoUrl();
    }
    return value;
  }

  private campaignLogoUrl(): string {
    let url = '';
    const logo = this.campaign ? this.campaign['logo'] : null;
    if (logo && typeof logo === 'object') {
      let file = logo.file;
      if (file !== null && typeof file !== 'string') {
        file = file['original'] ? file['original'] : file['small'];
      }
      url = file ? file : '';
    }
    return url;
  }

  private newUid(key: string): string {
    this.uidCounter++;
    return key + '#' + this.uidCounter;
  }

  private ensureRequiredSections(runtime: OmpRuntimeSection[], defaults: string[]) {
    defaults.forEach((key) => {
      const def = ompSectionDef(key);
      const present = runtime.some((section) => section.key === key);
      if (!def.deletable && !present) {
        runtime.splice(this.requiredInsertIndex(runtime, def, defaults), 0, {
          key,
          data: {},
          visible: def.defaultVisible,
          uid: this.newUid(key),
        });
      }
    });
  }

  private requiredInsertIndex(runtime: OmpRuntimeSection[], def: OmpSectionDef, defaults: string[]): number {
    let index = this.zoneInsertIndex(runtime, def);
    const rank = defaults.indexOf(def.key);
    if (def.position === OmpSectionPosition.Body && rank >= 0) {
      const bodyStart = this.bodyStartOf(runtime);
      let found = -1;
      runtime.forEach((section, at) => {
        const sectionRank = defaults.indexOf(section.key);
        if (found < 0 && at >= bodyStart && sectionRank > rank) {
          found = at;
        }
      });
      if (found >= 0) {
        index = found;
      }
    }
    return index;
  }

  private zoneInsertIndex(runtime: OmpRuntimeSection[], def: OmpSectionDef): number {
    let index = runtime.length;
    if (def.position === OmpSectionPosition.Top) {
      index = this.topInsertIndex(runtime, def.key);
    } else if (def.position === OmpSectionPosition.Body) {
      index = this.bodyStartOf(runtime);
      runtime.forEach((section, at) => {
        if (at >= index && this.sectionDef(section.key).position === OmpSectionPosition.Body) {
          index = at + 1;
        }
      });
    }
    return index;
  }

  private setSectionsFromCampaign() {
    let config = {};
    if (this.campaign && this.campaign.portal_settings && this.campaign.portal_settings.sections_config) {
      config = this.campaign.portal_settings.sections_config;
    }
    const norm = this.normalizeSectionsConfig(config);
    this.pageMoments = {};

    const toRuntime = (items: any) =>
      (Array.isArray(items) ? items : []).map((item) => {
        const def = ompSectionDef(item.key);
        const data = item.data ? JSON.parse(JSON.stringify(item.data)) : {};
        const visible = item.is_visible !== undefined ? item.is_visible : def.defaultVisible;
        return { key: item.key, data, visible, uid: this.newUid(item.key) } as OmpRuntimeSection;
      });
    const seed = (keys: string[]) =>
      keys.map((key) => {
        const def = ompSectionDef(key);
        return { key, data: {}, visible: def.defaultVisible, uid: this.newUid(key) } as OmpRuntimeSection;
      });

    this.pagesWithSections.forEach((page) => {
      const moments = {};
      ompMomentsForPage(page).forEach((moment) => {
        const momentKey = OmpMomentKey[moment];
        const saved = norm.pages[page] ? norm.pages[page][momentKey] : null;
        const defaults = ompDefaultPageSections(page, moment);
        let runtime = toRuntime(saved);
        if (page === OmpPage.Home) {
          this.convertHomeNextOrderDetails(runtime, moment);
        }
        if (runtime.length === 0) {
          runtime = seed(defaults);
        }
        runtime = runtime.filter(
          (section) => defaults.indexOf(section.key) >= 0 || this.sectionDef(section.key).deletable
        );
        this.ensureRequiredSections(runtime, defaults);
        runtime.forEach((section) => this.ensureSectionData(section, page, moment));
        moments[momentKey] = runtime;
      });
      this.pageMoments[page] = moments;
    });

    this.closeSettings();
    this.sectionsBaseline = norm.migrated ? canonicalJson(config) : this.sectionsSnapshot();
    this.sectionsChanged = norm.migrated;
    this.pushPreview(OmpPreviewReason.Structure);
  }

  private convertHomeNextOrderDetails(runtime: OmpRuntimeSection[], moment: OmpMoment) {
    const hasMomentCard = runtime.some((section) => section.key === OmpSectionKey.MomentCard);
    runtime.forEach((section) => {
      if (section.key === OmpSectionKey.NextOrderDetails && !hasMomentCard) {
        section.key = OmpSectionKey.MomentCard;
        section.data = ompMomentCardFromNextOrderDetails(section.data, moment);
        section.uid = this.newUid(OmpSectionKey.MomentCard);
      }
    });
  }

  private normalizeSectionsConfig(config: any): { pages: any; migrated: boolean } {
    const pages = {};
    let migrated = false;

    const fanOut = (page: string, list: any[]) => {
      const moments = {};
      ompMomentsForPage(page).forEach((moment) => {
        moments[OmpMomentKey[moment]] = list;
      });
      return moments;
    };

    const momentAtTop = OmpMomentList.some((moment) => {
      const value = config[OmpMomentKey[moment]];
      return value && !Array.isArray(value);
    });

    if (momentAtTop) {
      OmpMomentList.forEach((moment) => {
        const momentKey = OmpMomentKey[moment];
        const momentConfig = config[momentKey] ? config[momentKey] : {};
        this.pagesWithSections.forEach((page) => {
          if (momentConfig[page] && ompPageHasMoment(page, moment)) {
            if (!pages[page]) {
              pages[page] = {};
            }
            pages[page][momentKey] = momentConfig[page];
          }
        });
      });
      migrated = true;
    } else {
      this.pagesWithSections.forEach((page) => {
        const saved = config[page];
        if (Array.isArray(saved)) {
          if (ompPageHasMoments(page)) {
            migrated = true;
          }
          pages[page] = fanOut(page, saved);
        } else if (saved && typeof saved === 'object') {
          pages[page] = { ...saved };
        }
      });
    }

    return { pages: pages, migrated: migrated };
  }

  get showMoments(): boolean {
    return ompPageHasMoments(this.selectedPage);
  }

  get isDefaultMoment(): boolean {
    return this.selectedMoment === OmpDefaultMoment;
  }

  get syncMomentsLabel(): string {
    let label = 'Sync Sections from Default';
    if (this.isDefaultMoment) {
      label = 'Sync Sections to All Moments';
    }
    return label;
  }

  onSyncMoments() {
    this.syncMoments.emit();
  }

  sectionName(key: string): string {
    return ompSectionName(key, this.selectedPage);
  }

  sectionDef(key: string): OmpSectionDef {
    return ompSectionDef(key);
  }

  pageHasSections() {
    return this.pagesWithSections.indexOf(this.selectedPage) >= 0;
  }

  syncSectionsFromDefault(currentMoment: OmpMoment) {
    const page = this.selectedPage;
    let targets = [currentMoment];
    if (currentMoment === OmpDefaultMoment) {
      targets = ompMomentsForPage(page).filter((moment) => moment !== OmpDefaultMoment);
    }
    const source = this.sectionsFor(page, OmpDefaultMoment);
    if (source.length && this.pageMoments[page]) {
      targets.forEach((moment) => {
        if (moment !== OmpDefaultMoment) {
          this.pageMoments[page][OmpMomentKey[moment]] = this.buildSyncedMoment(source, page, moment);
        }
      });
      this.labelNextStep(OmpHistoryLabel.Sync);
      this.sectionsChanged = true;
      this.pushPreview(OmpPreviewReason.Structure);
    }
  }

  private buildSyncedMoment(source: OmpRuntimeSection[], page: OmpPage, moment: OmpMoment): OmpRuntimeSection[] {
    const existing = this.sectionsFor(page, moment);
    const momentKey = ompMomentSectionKey(page);
    let momentSection: OmpRuntimeSection = null;
    existing.forEach((section) => {
      if (section.key === momentKey) {
        momentSection = section;
      }
    });
    if (!momentSection) {
      const def = ompSectionDef(momentKey);
      momentSection = {
        key: momentKey,
        data: {},
        visible: def.defaultVisible,
        uid: this.newUid(momentKey),
      };
    }
    this.ensureSectionData(momentSection, page, moment);

    const synced: OmpRuntimeSection[] = [];
    source.forEach((section) => {
      if (section.key !== momentKey) {
        const copy: OmpRuntimeSection = {
          key: section.key,
          data: JSON.parse(JSON.stringify(section.data ? section.data : {})),
          visible: section.visible,
          uid: this.newUid(section.key),
        };
        this.ensureSectionData(copy, page, moment);
        synced.push(copy);
      }
    });
    synced.splice(this.bodyStartOf(synced), 0, momentSection);
    return synced;
  }

  private sectionsFor(page: OmpPage, moment: OmpMoment): OmpRuntimeSection[] {
    let list: OmpRuntimeSection[] = [];
    const moments = this.pageMoments[page];
    const momentKey = OmpMomentKey[ompPageHasMoments(page) ? moment : OmpMoment.None];
    if (moments && moments[momentKey]) {
      list = moments[momentKey];
    }
    return list;
  }

  currentSections(): OmpRuntimeSection[] {
    return this.sectionsFor(this.selectedPage, this.selectedMoment);
  }

  bodyStartIndex(): number {
    return this.bodyStartOf(this.currentSections());
  }

  private bodyStartOf(list: OmpRuntimeSection[]): number {
    let index = 0;
    list.forEach((section, at) => {
      if (this.sectionDef(section.key).position === OmpSectionPosition.Top) {
        index = at + 1;
      }
    });
    return index;
  }

  bodyEndIndex(): number {
    const list = this.currentSections();
    let bottom = 0;
    list.forEach((s) => {
      if (this.sectionDef(s.key).position === OmpSectionPosition.Bottom) {
        bottom++;
      }
    });
    return list.length - 1 - bottom;
  }

  onSectionDrop(event: CdkDragDrop<OmpRuntimeSection[]>) {
    const list = this.currentSections();
    let to = event.currentIndex;
    if (to < this.bodyStartIndex()) {
      to = this.bodyStartIndex();
    }
    if (to > this.bodyEndIndex()) {
      to = this.bodyEndIndex();
    }
    if (event.previousIndex !== to) {
      moveItemInArray(list, event.previousIndex, to);
      this.labelNextStep(OmpHistoryLabel.Reorder);
      this.sectionsChanged = true;
      this.pushPreview(OmpPreviewReason.Structure);
    }
  }

  setSectionVisible(section: OmpRuntimeSection, visible: boolean) {
    section.visible = visible;
    this.labelNextStep(this.visibilityLabel(section));
    this.sectionsChanged = true;
    this.pushPreview(OmpPreviewReason.Structure);
  }

  toggleSectionVisible(section: OmpRuntimeSection) {
    section.visible = !section.visible;
    this.labelNextStep(this.visibilityLabel(section));
    this.sectionsChanged = true;
    this.pushPreview(OmpPreviewReason.Structure);
  }

  deleteSectionItem(section: OmpRuntimeSection) {
    this.pendingDelete = section;
    this.modalService.getModal('ompDeleteSectionModal').open();
  }

  get pendingDeleteName(): string {
    let name = 'this section';
    if (this.pendingDelete) {
      name = ompSectionName(this.pendingDelete.key, this.selectedPage);
    }
    return name;
  }

  cancelDeleteSection() {
    this.pendingDelete = null;
    this.modalService.getModal('ompDeleteSectionModal').close();
  }

  confirmDeleteSection() {
    const section = this.pendingDelete;
    this.pendingDelete = null;
    this.modalService.getModal('ompDeleteSectionModal').close();
    if (section) {
      const list = this.currentSections();
      const index = list.indexOf(section);
      if (index >= 0) {
        if (this.openSettingsSection === section) {
          this.closeSettings();
        }
        list.splice(index, 1);
        this.labelNextStep(OmpHistoryLabel.Delete + ' ' + ompSectionName(section.key, this.selectedPage));
        this.sectionsChanged = true;
        this.pushPreview(OmpPreviewReason.Structure);
      }
    }
  }

  requestDeleteSection(uid: string) {
    const section = this.currentSections().find((s) => s.uid === uid);
    if (section && this.sectionDef(section.key).deletable) {
      this.deleteSectionItem(section);
    }
  }

  openSectionPicker(insertIndex?: number, zone?: OmpSectionPosition) {
    this.pickerSelectedKey = null;
    this.pendingInsertIndex = insertIndex === undefined || insertIndex === null ? null : insertIndex;
    this.pickerZone = zone ? zone : null;
    if (this.visiblePickerSections().length) {
      this.modalService.getModal('ompAddSectionModal').open();
    } else {
      this.pendingInsertIndex = null;
    }
  }

  visiblePickerSections(): OmpSectionDef[] {
    let list = this.addableSections;
    if (!this.isStaff) {
      list = list.filter((def) => !def.adminOnly);
    }
    if (this.pickerZone) {
      list = list.filter((def) => def.position === this.pickerZone);
    }
    return list;
  }

  closeSectionPicker() {
    this.pendingInsertIndex = null;
    this.modalService.getModal('ompAddSectionModal').close();
  }

  canAddSection(def: OmpSectionDef): boolean {
    let allowed = def.hasDetailSettings && this.isAllowedOnPage(def);
    if (allowed && !def.repeatable) {
      allowed = !this.isAlreadyAdded(def);
    }
    return allowed;
  }

  isAllowedOnPage(def: OmpSectionDef): boolean {
    return !def.pages || def.pages.indexOf(this.selectedPage) !== -1;
  }

  allowedPagesLabel(def: OmpSectionDef): string {
    const labels: string[] = [];
    OmpPages.forEach((page) => {
      if (def.pages && def.pages.indexOf(page.key) !== -1) {
        labels.push(page.label);
      }
    });
    return labels.join(', ');
  }

  isAlreadyAdded(def: OmpSectionDef): boolean {
    return this.currentSections().some((s) => s.key === def.key);
  }

  isAtSectionLimit(def: OmpSectionDef): boolean {
    return !def.repeatable && this.isAlreadyAdded(def);
  }

  sectionRowLabel(section: OmpRuntimeSection): string {
    const def = this.sectionDef(section.key);
    let label = ompSectionName(section.key, this.selectedPage);
    if (def.repeatable) {
      const siblings = this.currentSections().filter((s) => s.key === section.key);
      if (siblings.length > 1) {
        const hint = this.sectionHeadingExcerpt(section);
        const tellsApart = !!hint && siblings.filter((s) => this.sectionHeadingExcerpt(s) === hint).length === 1;
        if (tellsApart) {
          label = ompSectionName(section.key, this.selectedPage) + ' — ' + hint;
        } else {
          label = ompSectionName(section.key, this.selectedPage) + ' ' + (siblings.indexOf(section) + 1);
        }
      }
    }
    return label;
  }

  private sectionHeadingExcerpt(section: OmpRuntimeSection): string {
    const data = section.data ? section.data : {};
    let raw = '';
    OmpSectionRowHintFields.forEach((field) => {
      if (!raw && typeof data[field] === 'string' && data[field]) {
        raw = data[field];
      }
    });
    const text = raw.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > OmpSectionRowHintLength ? text.substring(0, OmpSectionRowHintLength) + '…' : text;
  }

  selectPickerSection(def: OmpSectionDef) {
    if (this.canAddSection(def)) {
      this.pickerSelectedKey = def.key;
    }
  }

  isPickerSelected(def: OmpSectionDef) {
    return this.pickerSelectedKey === def.key;
  }

  confirmAddSection() {
    if (this.pickerSelectedKey) {
      const list = this.currentSections();
      const key = this.pickerSelectedKey;
      const newItem: OmpRuntimeSection = { key, data: {}, visible: true, uid: this.newUid(key) };
      let insertAt: number;
      if (this.sectionDef(key).position === OmpSectionPosition.Top) {
        insertAt = this.topInsertIndex(list, key);
      } else if (this.pendingInsertIndex !== null) {
        insertAt = Math.min(Math.max(this.pendingInsertIndex, this.bodyStartIndex()), this.bodyEndIndex() + 1);
      } else {
        insertAt = this.bodyEndIndex() + 1;
      }
      list.splice(insertAt, 0, newItem);
      this.labelNextStep(OmpHistoryLabel.Add + ' ' + ompSectionName(key, this.selectedPage));
      this.sectionsChanged = true;
      this.pushPreview(OmpPreviewReason.Structure);
      this.closeSectionPicker();
      this.openSectionSettings(newItem, true);
    }
  }

  private topInsertIndex(list: OmpRuntimeSection[], key: string): number {
    const rank = OmpTopSectionOrder.indexOf(key);
    let idx = 0;
    let scanning = true;
    while (scanning && idx < list.length) {
      const def = this.sectionDef(list[idx].key);
      const itemRank = OmpTopSectionOrder.indexOf(list[idx].key);
      if (def.position !== OmpSectionPosition.Top || itemRank > rank) {
        scanning = false;
      } else {
        idx++;
      }
    }
    return idx;
  }

  openSectionSettings(section: OmpRuntimeSection, reveal: boolean = false) {
    if (this.sectionDef(section.key).hasDetailSettings) {
      this.ensureSectionData(section, this.selectedPage, this.selectedMoment);
      this.settingsStack = [section];
      this.openSettingsSection = section;
    }
    this.emitSelection(reveal);
  }

  openChildSettings(child: OmpPageSectionItem) {
    if (child && isOmpColumnChildKey(child.key)) {
      const section: OmpRuntimeSection = { key: child.key, data: child.data, visible: true };
      this.ensureSectionData(section, this.selectedPage, this.selectedMoment);
      child.data = section.data;
      this.settingsStack.push(section);
      this.openSettingsSection = section;
      this.emitSelection();
    }
  }

  backToSections() {
    this.settingsStack.pop();
    this.openSettingsSection = this.settingsStack.length ? this.settingsStack[this.settingsStack.length - 1] : null;
    this.emitSelection();
  }

  settingsColumnCount(): number {
    let count = 1;
    if (this.settingsStack.length > 1) {
      const parent = this.settingsStack[this.settingsStack.length - 2];
      const parentCount = parent && parent.data ? Number(parent.data.column_count) : 0;
      if (parentCount > 0) {
        count = parentCount;
      }
    }
    return count;
  }

  settingsBreadcrumb(): string[] {
    return this.settingsStack.map((section) => ompSectionName(section.key, this.selectedPage));
  }

  private closeSettings() {
    this.settingsStack = [];
    this.openSettingsSection = null;
    this.emitSelection();
  }

  private ensureSectionData(section: OmpRuntimeSection, page: string, moment: OmpMoment) {
    if (!section.data) {
      section.data = {};
    }
    this.renameRetiredFields(section);
    section.data = seedOmpSectionData(section.key, section.data, page, moment, this.brandVariables);
    this.ensureChildSectionData(section, page, moment);
  }

  private ensureChildSectionData(section: OmpRuntimeSection, page: string, moment: OmpMoment) {
    if (section.data && Array.isArray(section.data[OmpColumnsChildField])) {
      const children = section.data[OmpColumnsChildField].filter((child) => child && isOmpColumnChildKey(child.key));
      children.forEach((child) => {
        child.data = seedOmpSectionData(child.key, child.data, page, moment, this.brandVariables);
      });
      section.data[OmpColumnsChildField] = children;
      section.data.column_count = children.length;
    }
  }

  onSectionDataChange() {
    this.sectionsChanged = true;
    this.pushPreview(OmpPreviewReason.Data);
  }

  private noReasons(): { [reason: string]: boolean } {
    return {};
  }

  private pushPreview(reason: OmpPreviewReason) {
    this.pendingReasons[reason] = true;
    this.pendingDataPage = this.selectedPage;
    this.pendingDataMoment = this.currentPageMoment();
    this.previewPush$.next(reason);
  }

  private currentMomentKey(): string {
    return OmpMomentKey[this.currentPageMoment()];
  }

  private currentPageMoment(): OmpMoment {
    return ompPageHasMoments(this.selectedPage) ? this.selectedMoment : OmpMoment.None;
  }

  private sendPreviewInitWhenLoaded() {
    if (this.previewConnected && this.styleLoaded) {
      this.sendPreviewInit();
    }
  }

  private sendPreviewInit() {
    this.pendingReasons = this.noReasons();
    this.lastSentSelectionUid = null;
    this.previewBridge.send(OmpHostMessageType.Init, {
      subdomainType: OMP_PREVIEW_SUBDOMAIN_TYPE,
      campaign: this.previewCampaign(),
      theme: this.buildOverrides(),
      portalSettings: { sections_config: this.buildPreviewSectionsConfig() },
      page: this.selectedPage,
      moment: this.currentMomentKey(),
      device: '',
      selectedUid: this.openSettingsSection ? this.openSettingsSection.uid : null,
    });
    this.lastSentFaqSignature = null;
    this.syncPreviewFaqs();
  }

  private previewCampaign() {
    const campaign: Partial<Campaign> = this.campaign ? this.campaign : {};
    return {
      id: campaign.id,
      name: campaign.name,
      subdomain: campaign.subdomain,
      logo: this.campaignLogoUrl(),
      display_name: campaign.display_name,
      support_email: campaign.support_email,
      support_phone: campaign.support_phone,
    };
  }

  private sendPreviewUpdate() {
    const pending = this.pendingReasons;
    this.pendingReasons = this.noReasons();

    if (pending[OmpPreviewReason.Theme]) {
      this.previewBridge.send(OmpHostMessageType.Theme, { theme: this.buildOverrides() });
    }
    if (pending[OmpPreviewReason.Structure]) {
      this.previewBridge.send(OmpHostMessageType.Config, {
        portalSettings: { sections_config: this.buildPreviewSectionsConfig() },
      });
    } else if (pending[OmpPreviewReason.Data]) {
      const page = this.pendingDataPage;
      const moment = this.pendingDataMoment;
      this.previewBridge.send(OmpHostMessageType.ConfigPage, {
        page: page,
        moment: OmpMomentKey[ompPageHasMoments(page) ? moment : OmpMoment.None],
        sections: this.buildPreviewSections(this.sectionsFor(page, moment), page, moment),
      });
    }
    if (pending[OmpPreviewReason.Structure] || pending[OmpPreviewReason.Data]) {
      this.syncPreviewFaqs();
    }
  }

  private loadPreviewFaqTopics() {
    this.faqTopicService.list().pipe(takeUntil(this.destroyed$)).subscribe(
      (topics: any[]) => {
        this.previewFaqTopics = (topics ? topics : []).map((topic) => ({ id: +topic.id, title: topic.title }));
        this.syncPreviewFaqs();
      },
      () => {
        this.previewFaqTopics = [];
      }
    );
  }

  private syncPreviewFaqs() {
    // Resolve the FAQ ids selected across every FAQ card, then push them to the preview.
    const ids = this.previewSelectedFaqIds();
    const signature = ids.slice().sort().join(',');
    if (signature === this.lastRequestedFaqSignature) {
      this.sendPreviewFaqs();
    } else {
      this.lastRequestedFaqSignature = signature;
      if (ids.length === 0) {
        this.previewSelectedFaqs = [];
        this.sendPreviewFaqs();
      } else {
        this.faqService.getFaqsByIds(ids).pipe(takeUntil(this.destroyed$)).subscribe(
          (response: any) => {
            const list = Array.isArray(response) ? response : [];
            this.previewSelectedFaqs = list.map((faq) => ({ id: +faq.id, question: faq.question, answer: faq.answer }));
            this.sendPreviewFaqs();
          },
          () => {
            this.previewSelectedFaqs = [];
            this.sendPreviewFaqs();
          }
        );
      }
    }
  }

  private previewSelectedFaqIds(): number[] {
    const ids: number[] = [];
    const collect = (list: OmpRuntimeSection[]) => {
      (list ? list : []).forEach((section) => {
        if (section.key === OmpSectionKey.FaqSection && section.data && section.data.faq_ids) {
          section.data.faq_ids.forEach((id) => {
            const num = +id;
            if (num > 0 && ids.indexOf(num) === -1) {
              ids.push(num);
            }
          });
        }
      });
    };
    this.forEachPageMoment((list) => collect(list));
    return ids;
  }

  private sendPreviewFaqs() {
    const faqs = this.previewSelectedFaqs ? this.previewSelectedFaqs : [];
    const signature = faqs.map((faq) => faq.id).join(',') + '|' + this.previewFaqTopics.length;
    if (signature !== this.lastSentFaqSignature) {
      this.lastSentFaqSignature = signature;
      this.previewBridge.send(OmpHostMessageType.Faqs, {
        topics: this.previewFaqTopics,
        faqs: faqs,
      });
    }
  }

  public get sampleDataNote(): string {
    return this.openSettingsSection ? ompSampleDataNote(this.openSettingsSection.key) : '';
  }

  private emitSelection(reveal: boolean = false) {
    let uid: string = null;
    for (let i = this.settingsStack.length - 1; i >= 0 && !uid; i--) {
      uid = this.settingsStack[i].uid ? this.settingsStack[i].uid : null;
    }
    if (uid !== this.lastSentSelectionUid) {
      const payload: OmpHostSelectPayload = { uid: uid };
      if (reveal && uid) {
        payload.reveal = true;
      }
      this.lastSentSelectionUid = uid;
      this.previewBridge.send(OmpHostMessageType.Select, payload);
    }
  }

  onSectionRowEnter(section: OmpRuntimeSection) {
    this.emitHover(section.uid);
  }

  onSectionRowLeave() {
    this.emitHover(null);
  }

  private emitHover(uid: string) {
    if (uid !== this.lastSentHoverUid) {
      this.lastSentHoverUid = uid;
      this.previewBridge.send(OmpHostMessageType.Hover, { uid: uid });
    }
  }

  requestReveal(target: OmpRevealTarget) {
    this.pendingReveal = target;
  }

  applyPendingReveal() {
    const target = this.pendingReveal;
    this.pendingReveal = null;
    let section: OmpRuntimeSection = null;
    if (target) {
      section = this.currentSections().find((s) => s.uid === target.uid);
    }
    if (section) {
      this.openSectionSettings(section);
      if (target.childIndex !== undefined && target.childIndex !== null && section.data) {
        const children = section.data[OmpColumnsChildField];
        if (Array.isArray(children) && children[target.childIndex]) {
          this.openChildSettings(children[target.childIndex]);
        }
      }
    }
  }

  applyPreviewReorder(order: string[]) {
    const list = this.currentSections();
    const byUid: { [uid: string]: OmpRuntimeSection } = {};
    list.forEach((section) => {
      if (section.uid) {
        byUid[section.uid] = section;
      }
    });

    const requested: OmpRuntimeSection[] = [];
    (order || []).forEach((uid) => {
      if (byUid[uid] && requested.indexOf(byUid[uid]) < 0) {
        requested.push(byUid[uid]);
      }
    });
    list.forEach((section) => {
      if (requested.indexOf(section) < 0) {
        requested.push(section);
      }
    });

    const zoneOf = (section: OmpRuntimeSection) => this.sectionDef(section.key).position;
    const top = list.filter((section) => zoneOf(section) === OmpSectionPosition.Top);
    const body = requested.filter((section) => zoneOf(section) === OmpSectionPosition.Body);
    const bottom = list.filter((section) => zoneOf(section) === OmpSectionPosition.Bottom);
    const next = top.concat(body, bottom);

    const unchanged = next.length === list.length && next.every((section, index) => section === list[index]);
    if (!unchanged) {
      list.splice(0, list.length);
      next.forEach((section) => list.push(section));
      this.labelNextStep(OmpHistoryLabel.Reorder);
      this.sectionsChanged = true;
      this.pushPreview(OmpPreviewReason.Structure);
    }
  }

  requestAddSection(anchorUid: string, before: boolean, zone: OmpSectionPosition) {
    const list = this.currentSections();
    let index: number = null;
    list.forEach((section, at) => {
      if (section.uid === anchorUid) {
        index = before ? at : at + 1;
      }
    });
    this.openSectionPicker(index, zone);
  }

  onIconModalClose() {
    if (this.picker.hasPendingIcon()) {
      this.picker.completeIcon(null);
    }
  }

  openImageGallery(controlKey: string) {
    this.selectedImageControl = controlKey;
    this.modalService.getModal('ompImageGalleryModal').open();
  }

  onGallerySelect(image: any) {
    if (this.picker.hasPendingImage()) {
      this.picker.completeImage(image);
    } else {
      this.onImageSelected(image);
    }
  }

  onGalleryClose() {
    if (this.picker.hasPendingImage()) {
      this.picker.completeImage(null);
    } else {
      this.selectedImageControl = null;
    }
  }

  onImageSelected(image: any) {
    if (this.selectedImageControl && image) {
      let file = image.file;
      if (file !== null && typeof file !== 'string') {
        file = file['original'] ? file['original'] : file['small'];
      }
      this.form.get(this.selectedImageControl).setValue(file);
      this.modalService.getModal('ompImageGalleryModal').close();
      this.selectedImageControl = null;
    }
  }

  imagePreview(key: string) {
    let preview = '';
    const control = this.form.get(key);
    if (control && control.value) {
      preview = control.value;
    }
    return preview;
  }

  clearImage(controlKey: string, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.form.get(controlKey).setValue('');
  }

  rangeBackground(value: number, min: number, max: number) {
    let percent = 0;
    if (max > min) {
      percent = ((value - min) / (max - min)) * 100;
    }
    if (percent < 0) {
      percent = 0;
    }
    if (percent > 100) {
      percent = 100;
    }
    return `linear-gradient(to right, #2eb3c9 0%, #2eb3c9 ${percent}%, #ccc ${percent}%, #ccc 100%)`;
  }

  isTabActive(tab: OmpTab) {
    let active = false;
    if (this.activeTab === tab) {
      active = true;
    }
    return active;
  }

  isExpanded(section: OmpSection) {
    let expanded = false;
    if (this.expandedSection === section) {
      expanded = true;
    }
    return expanded;
  }

  toggleSection(section: OmpSection) {
    if (this.expandedSection === section) {
      this.expandedSection = null;
    } else {
      this.expandedSection = section;
    }
  }

  isButtonStyleExpanded(key: string) {
    let expanded = false;
    if (this.expandedButtonStyle === key) {
      expanded = true;
    }
    return expanded;
  }

  toggleButtonStyle(key: string) {
    if (this.expandedButtonStyle === key) {
      this.expandedButtonStyle = null;
    } else {
      this.expandedButtonStyle = key;
    }
  }
}
